import { FC, useContext, useEffect, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { DotsMenu, Label, Preloader } from '@smartshell/ui-kit';
import cn from 'classnames';
import { render, unmountComponentAtNode } from 'react-dom';

import { AuthContext } from 'auth';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  GET_WORKSHIFT_DATA,
  GetWorkShiftDataRequest,
  GOODS,
  IGetWorkShiftDataResponse,
} from 'schemas';
import { printWorkShiftReport } from 'services/printWorkShiftReport';
import { formatFIO } from 'services/printWorkShiftReport/utils';
import { SettingsContext } from 'settings';
import { TGood } from 'types';
import { formatDate, getFormattedDuration } from 'utils/workShifts';

import { IWorkShiftItemProps } from './interfaces';
import s from './WorkShiftItem.module.scss';
export const WorkShiftItem: FC<IWorkShiftItemProps> = ({
  workShift,
  isSelected,
  selectWorkShift,
  openWorkShiftDetails,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency, clubName, printReportAllGoods },
    },
  } = useContext(SettingsContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const size = useWindowResize();

  const { data } = useQuery<{ goods: Array<TGood> }>(GOODS, {
    variables: {
      input: {
        title_search: undefined,
        category_id: undefined,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !printReportAllGoods,
    notifyOnNetworkStatusChange: true,
  });

  const allGoods = data?.goods ?? [];
  const { id, starts, finish, workerFIO, cash, card, proceeds, bonuses, tips } =
    workShift;

  const [loadDataForReport] = useLazyQuery<
    IGetWorkShiftDataResponse,
    GetWorkShiftDataRequest
  >(GET_WORKSHIFT_DATA, { fetchPolicy: 'cache-first' });

  const [isGenerating, setIsGenerating] = useState(false);
  useEffect(() => {
    if (isGenerating) {
      const modalNode = document.getElementById('modal-root');

      if (!modalNode) return;

      render(
        <div className={s['overlay']}>
          <Preloader loading={true} />
        </div>,
        modalNode
      );

      return () => {
        unmountComponentAtNode(modalNode);
      };
    }
  }, [isGenerating]);

  return (
    <div
      className={cn(s.container, isSelected && s.selected)}
      onClick={() => selectWorkShift(id)}
      onDoubleClick={() => {
        if (!permissions?.has('work_shift_browse')) return;

        openWorkShiftDetails(id);
      }}
    >
      <div className={cn(s.column, s.time)}>
        {t('report.workShift.start')}
        <div className={s.data}>{formatDate(starts)}</div>
      </div>

      <div className={cn(s.column, s.time)}>
        {t('report.workShift.end')}
        <div className={s.data}>{formatDate(finish)}</div>
      </div>

      <div className={s.separator} />

      <div className={cn(s.column, s.worker)}>
        {t('report.workShift.worker')}
        <div className={s.data}>{workerFIO || '—'}</div>
      </div>
      <div className={cn(s.column, s.duration)}>
        {t('report.workShift.duration')}
        <div className={s.data}>{getFormattedDuration(starts, finish, t)}</div>
      </div>

      <div className={s.separator} />

      <div className={cn(s.column, s.sum, s.cash)}>
        {t('money.cash')}
        <div className={s.data}>
          {cash.toLocaleString()} {currency.sign}
        </div>
      </div>
      <div className={cn(s.column, s.sum)}>
        {t('money.card')}
        <div className={s.data}>
          {card.toLocaleString()} {currency.sign}
        </div>
      </div>
      <div className={cn(s.column, s.sum)}>
        {t('money.proceeds')}
        <div className={s.data}>
          {proceeds.toLocaleString()} {currency.sign}
        </div>
      </div>
      <div className={cn(s.column, s.sum)}>
        {t('tips')}
        <div className={s.data}>
          <Label
            text={`${tips.toLocaleString()} ${currency.sign}`}
            iconName="HandCoinIcon"
            color="green"
          />
        </div>
      </div>

      <div className={s.separator} />

      <div className={cn(s.column, s.bonus)}>
        {size.width > 960 && t('money.bonus')}
        <div className={s.data}>
          <Label
            text={`${bonuses.toLocaleString()} ${currency.sign}`}
            iconName="BonusIcon"
            color="orange"
            rounded
          />
        </div>
      </div>

      {permissions?.has('work_shift_browse') && (
        <DotsMenu
          data={null}
          items={[
            {
              key: 'details',
              iconName: 'OpenedEyeIcon',
              text: t('report.workShift.actions.details'),
              onClick: () => openWorkShiftDetails(workShift.id),
            },
            {
              key: 'print',
              iconName: 'PrinterIcon',
              text: t('report.workShift.actions.pdf'),
              onClick: () => {
                setIsGenerating(true);

                loadDataForReport({ variables: { id } })
                  .then(async (response) => {
                    if (!response.data) return null;

                    const {
                      id,
                      cash_orders,
                      cash_on_start,
                      cash,
                      card,
                      deposit,
                      online_deposit,
                      created_at,
                      bonus,
                      worker,
                      goods,
                      combos,
                      services,
                      finished_at,
                    } = response.data.getWorkShiftPaymentOverviewData;

                    const { comment } = response.data.workShift;

                    const cashOrdersDiff = cash_orders.reduce((acc, order) => {
                      if (order.type === 'PKO') {
                        return acc + order.sum;
                      }

                      if (order.type === 'RKO') {
                        return acc - order.sum;
                      }

                      return acc;
                    }, 0);

                    const inCashBox = cash_on_start + cash + cashOrdersDiff;

                    const goodsSorted = goods?.sort((a, b) =>
                      a.title.localeCompare(b.title)
                    );

                    const allGoodsSorted = allGoods
                      .filter(
                        ({ title }) =>
                          !goods?.some((good) => good.title === title)
                      )
                      .sort((a, b) => a.title.localeCompare(b.title));

                    return printWorkShiftReport({
                      clubName,
                      goods: goodsSorted,
                      services,
                      cashOrders: cash_orders,
                      id,
                      bonus,
                      worker: formatFIO(worker),
                      card,
                      cash,
                      cash_on_start,
                      comment,
                      currency: currency.sign,
                      deposit,
                      online_deposit,
                      starts: created_at,
                      finished: finished_at,
                      t,
                      inCashBox,
                      combos,
                      printReportAllGoods,
                      allGoods: allGoodsSorted,
                    });
                  })
                  .catch(console.error)
                  .finally(() => setIsGenerating(false));
              },
            },
          ]}
          type="horizontal"
          className={s.menu}
        />
      )}
    </div>
  );
};
